"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, RefreshCw } from "lucide-react";

export default function WebsiteAutoMenuScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div
        className="absolute top-1/3 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Menu source of truth", "Fuente única del menú")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Edit POS. ", "Editas el POS. ")}
            <span className="text-redy-charcoal/30">{t("Refreshes site.", "Refresca el sitio.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Your menu lives in one place: the POS. Change a price, 86 an item, add a special — the site re-renders in seconds. No designer call. No \"the website still shows the old price\" awkwardness.",
              "Tu menú vive en un solo lugar: el POS. Cambias un precio, agotas un item, agregas un especial — el sitio se re-renderiza en segundos. Sin llamar al diseñador. Sin la pena de \"el sitio aún muestra el precio viejo\"."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-6 items-center max-w-[920px] mx-auto"
        >
          {/* POS side */}
          <div className="bg-white border border-black/5 rounded-[20px] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                {t("POS · Menu Editor", "POS · Editor de menú")}
              </div>
            </div>

            <div className="space-y-2 mb-3">
              <div className="bg-[#FAFAF8] rounded-[10px] p-2.5 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-black text-redy-charcoal">Birria Taco</div>
                  <div className="text-[9px] text-redy-secondary">{t("Tacos category", "Categoría tacos")}</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] text-redy-secondary line-through">$4.00</div>
                  <motion.div
                    initial={{ scale: 1.2, color: "#F4722B" }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                    className="text-[14px] font-black tabular-nums"
                    style={{ color: "#F4722B" }}
                  >
                    $4.50
                  </motion.div>
                </div>
              </div>
              <div className="bg-[#FAFAF8] rounded-[10px] p-2.5 flex items-center justify-between opacity-60">
                <div>
                  <div className="text-[11px] font-black text-redy-charcoal">Carnitas</div>
                  <div className="text-[9px] text-redy-secondary">{t("Tacos category", "Categoría tacos")}</div>
                </div>
                <div className="text-[14px] font-black text-redy-charcoal tabular-nums">$4.00</div>
              </div>
            </div>

            <div className="text-[10px] font-medium text-redy-secondary text-center pt-2 border-t border-black/5">
              {t("Updated by Maria · just now", "Editado por Maria · ahora")}
            </div>
          </div>

          {/* Sync arrow */}
          <div className="flex flex-col items-center gap-2 py-4">
            <motion.div
              animate={{ x: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                boxShadow: "0 10px 30px rgba(244,114,43,0.30)",
              }}
            >
              <RefreshCw className="w-5 h-5 text-white" strokeWidth={3} />
            </motion.div>
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#D7263D" }}>
              {t("Live", "Live")}
            </div>
          </div>

          {/* Site side — browser */}
          <div>
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
              {t("Public site · live", "Sitio público · en vivo")}
            </div>
            <div className="rounded-[14px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.06)] border border-black/5">
              <div className="px-3 py-2 bg-[#FAFAF8] border-b border-black/5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" />
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" />
                  <div className="w-1.5 h-1.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="flex-1 bg-white rounded px-2 py-0.5 text-[8px] text-redy-secondary truncate">
                  pepitos.useredy.com
                </div>
              </div>
              <div className="bg-white p-3 space-y-2">
                <div className="text-[11px] font-black text-redy-charcoal mb-1">{t("Menu", "Menú")}</div>
                <motion.div
                  initial={{ backgroundColor: "rgba(244,114,43,0)" }}
                  whileInView={{ backgroundColor: "rgba(244,114,43,0.10)" }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="rounded-[8px] p-2 flex items-center justify-between"
                >
                  <span className="text-[10px] font-black text-redy-charcoal">Birria Taco</span>
                  <motion.span
                    initial={{ scale: 1.15 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.4, delay: 1.0 }}
                    className="text-[11px] font-black tabular-nums"
                    style={{ color: "#F4722B" }}
                  >
                    $4.50
                  </motion.span>
                </motion.div>
                <div className="rounded-[8px] p-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-redy-charcoal">Carnitas</span>
                  <span className="text-[10px] font-bold tabular-nums text-redy-charcoal">$4.00</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
